import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Package } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useCreateSale } from "@/hooks/useCreateSale";
import { toast } from "sonner";

interface AddServiceToCaseModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  caseId: string;
  onSuccess?: () => void;
}

interface CatalogService {
  id: string;
  name: string;
  base_price: number;
  description?: string;
}

export default function AddServiceToCaseModal({
  open,
  onOpenChange,
  caseId,
  onSuccess,
}: AddServiceToCaseModalProps) {
  const [services, setServices] = useState<CatalogService[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [selectedServiceId, setSelectedServiceId] = useState("");
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [installments, setInstallments] = useState("1");
  const [firstDueDate, setFirstDueDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  });

  const { createSaleForCase } = useCreateSale();

  useEffect(() => {
    if (open) {
      loadServices();
      resetForm();
    }
  }, [open]);

  const resetForm = () => {
    setSelectedServiceId("");
    setTotalAmount(0);
    setInstallments("1");
    const today = new Date();
    setFirstDueDate(today.toISOString().split("T")[0]);
  };

  const loadServices = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("catalog")
        .select("id, name, base_price, description")
        .eq("type", "service")
        .eq("is_active", true)
        .order("name");

      if (error) throw error;
      setServices(data || []);
    } catch (error) {
      console.error("Error loading services:", error);
      toast.error("Erro ao carregar serviços");
    }
    setLoading(false);
  };

  const handleServiceChange = (serviceId: string) => {
    setSelectedServiceId(serviceId);
    const service = services.find((s) => s.id === serviceId);
    if (service) {
      setTotalAmount(service.base_price || 0);
    }
  };

  const handleSubmit = async () => {
    if (!selectedServiceId) {
      toast.error("Selecione um serviço");
      return;
    }
    if (totalAmount <= 0) {
      toast.error("Informe um valor válido");
      return;
    }
    if (!firstDueDate) {
      toast.error("Informe a data da primeira parcela");
      return;
    }

    setSaving(true);
    try {
      await createSaleForCase({
        caseId,
        catalogId: selectedServiceId,
        totalAmount,
        installments: parseInt(installments),
        firstDueDate: new Date(firstDueDate),
      });

      toast.success("Serviço adicionado com sucesso!");
      onOpenChange(false);
      onSuccess?.();
    } catch (error: any) {
      console.error("Error creating sale:", error);
      toast.error(error.message || "Erro ao adicionar serviço");
    }
    setSaving(false);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);
  };

  const installmentAmount = totalAmount / parseInt(installments || "1");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            Adicionar Serviço
          </DialogTitle>
          <DialogDescription>
            Adicione um serviço a este case. O valor será somado ao deal
            existente.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="service">Serviço</Label>
              <Select
                value={selectedServiceId}
                onValueChange={handleServiceChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um serviço" />
                </SelectTrigger>
                <SelectContent>
                  {services.map((service) => (
                    <SelectItem key={service.id} value={service.id}>
                      {service.name} - {formatCurrency(service.base_price)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="amount">Valor Total</Label>
              <Input
                id="amount"
                type="number"
                min="0"
                step="0.01"
                value={totalAmount}
                onChange={(e) => setTotalAmount(parseFloat(e.target.value) || 0)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="installments">Número de Parcelas</Label>
              <Select value={installments} onValueChange={setInstallments}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((n) => (
                    <SelectItem key={n} value={n.toString()}>
                      {n}x {formatCurrency(totalAmount / n)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="dueDate">Data da Primeira Parcela</Label>
              <Input
                id="dueDate"
                type="date"
                value={firstDueDate}
                onChange={(e) => setFirstDueDate(e.target.value)}
              />
            </div>

            {totalAmount > 0 && (
              <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4 text-sm">
                <p className="font-medium text-slate-700 dark:text-slate-300">
                  Resumo:
                </p>
                <p className="text-slate-600 dark:text-slate-400 mt-1">
                  {installments}x de {formatCurrency(installmentAmount)}
                </p>
                <p className="text-slate-600 dark:text-slate-400">
                  Total: {formatCurrency(totalAmount)}
                </p>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={saving || loading}>
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Adicionar Serviço
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
